import React, { useState, Fragment } from 'react';
import {
  Accordion,
  AccordionItem,
  AccordionItemButton,
  AccordionItemHeading,
  AccordionItemPanel,
} from 'react-accessible-accordion';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { Formik, Form, Field, FieldArray } from 'formik';
import CustomLoader from '../../../common/CustomLoader';
import showAlert from '../../../../common/utils/alert';

export const ListDerivedFieldMappings = ({
  collections,
  actions,
  commonActions,
  projectId,
  version,
  isReadOnly,
}) => {
  const { deleteCollectionDerivedFieldMappingPending, selectedCollection, derivedFieldMapping } =
    collections;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMapping, setSelectedMapping] = useState(null);

  const toggleModal = () => setIsModalOpen(!isModalOpen);

  const handleEdit = (mapping) => {
    setSelectedMapping(mapping);
    setIsModalOpen(true);
  };

  const handleDelete = (mappingId) => {
    const preConfirm = () => {
      actions.deleteCollectionDerivedFieldMapping({
        projectId,
        version,
        mappingId,
        selectedCollection,
      });
      commonActions.trackCollectionChange({
        uuid: selectedCollection.uuid,
        name: selectedCollection.collectionName,
      });
    };
    showAlert("You won't be able to revert this.", preConfirm, 'Yes, delete it!');
  };

  const handleUpdate = (mappingId, values) => {
    const finalMappings = values.derivedFieldMapping
      .filter((m) => m.fieldName && m.derivedField)
      .map((m) => ({
        fieldName: m.fieldName,
        derivedField: selectedCollection.utilities.find((u) => u.name === m.derivedField) || {},
      }));

    const updatedValues = {
      ...values,
      derivedFieldMapping: finalMappings,
    };
    const preConfirm = () => {
      actions.updateCollectionDerivedFieldMapping({
        projectId,
        version,
        mappingId,
        selectedCollection,
        updatedValues,
      });
      toggleModal();
    };

    showAlert('Do you want to proceed?', preConfirm, 'Yes, update it!', 'info');
  };

  if (derivedFieldMapping.length > 0) {
    return (
      <>
        <Accordion allowZeroExpanded>
          {derivedFieldMapping.map((mapping, index) => (
            <AccordionItem key={index}>
              <AccordionItemHeading>
                <AccordionItemButton>
                  <strong>{mapping.mappingName}</strong>
                </AccordionItemButton>
              </AccordionItemHeading>
              <AccordionItemPanel>
                <Fragment>
                  <div className="card">
                    <div
                      className="card-header card-header-text"
                      data-background-color="drapcode-violet"
                    >
                      <h6 className="card-title pb-0 mb-0 text-capitalize">
                        {mapping.mappingName}
                      </h6>
                    </div>
                    <div className="card-content">
                      <div className="table-responsive">
                        <table className="table table-bordered table-grey connector-list">
                          <thead>
                            <tr>
                              <th>Field Name</th>
                              <th>Derived Field</th>
                            </tr>
                          </thead>
                          <tbody>
                            {mapping.derivedFieldMapping.length > 0 ? (
                              mapping.derivedFieldMapping.map((m, i) => (
                                <tr key={i}>
                                  <td>{m.fieldName}</td>
                                  <td>{m.derivedField.name || m.derivedField.fieldName}</td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="2" className="text-center">
                                  No Mappings!
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                      <div className="col-12 text-right my-2">
                        <button
                          type="button"
                          className="btn btn-sm btn-theme-submit"
                          onClick={() => (isReadOnly ? null : handleEdit(mapping))}
                          disabled={deleteCollectionDerivedFieldMappingPending || isReadOnly}
                          title={isReadOnly && 'Read-Only Mode'}
                        >
                          <i className="wb webconnect-pencil"></i> Edit&nbsp;
                          <CustomLoader loading={deleteCollectionDerivedFieldMappingPending} />
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-theme-delete ml-2"
                          onClick={() => (isReadOnly ? null : handleDelete(mapping.uuid))}
                          disabled={deleteCollectionDerivedFieldMappingPending || isReadOnly}
                          title={isReadOnly && 'Read-Only Mode'}
                        >
                          <i className="wb webconnect-trash-regular"></i> Delete&nbsp;
                          <CustomLoader loading={deleteCollectionDerivedFieldMappingPending} />
                        </button>
                      </div>
                    </div>
                  </div>
                </Fragment>
              </AccordionItemPanel>
            </AccordionItem>
          ))}
        </Accordion>

        {selectedMapping && (
          <Modal isOpen={isModalOpen} toggle={toggleModal} backdrop="static">
            <ModalHeader toggle={toggleModal}>Edit Mapping</ModalHeader>

            <Formik
              initialValues={{
                mappingName: selectedMapping.mappingName || '',
                uuid: selectedMapping.uuid || '',
                derivedFieldMapping: [
                  ...(selectedMapping.derivedFieldMapping || []).map((m) => ({
                    fieldName: m.fieldName,
                    derivedField: m.derivedField.name || '',
                  })),
                  { fieldName: '', derivedField: '' },
                ],
              }}
              onSubmit={(values) =>
                isReadOnly ? null : handleUpdate(selectedMapping.uuid, values)
              }
              enableReinitialize
            >
              {({ values, setFieldValue }) => (
                <Form>
                  <ModalBody>
                    <div className="mb-3">
                      <label>Mapping Name</label>
                      <Field className="form-control" name="mappingName" />
                    </div>

                    <FieldArray name="derivedFieldMapping">
                      {({ push, remove }) => (
                        <table className="table table-bordered">
                          <thead>
                            <tr>
                              <th>Collection Field</th>
                              <th>Derived Field</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {values.derivedFieldMapping.map((m, index) => (
                              <tr key={index}>
                                <td>
                                  <Field
                                    as="select"
                                    className="form-control"
                                    name={`derivedFieldMapping[${index}].fieldName`}
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      setFieldValue(`derivedFieldMapping[${index}].fieldName`, val);
                                      if (index === values.derivedFieldMapping.length - 1 && val) {
                                        push({ fieldName: '', derivedField: '' });
                                      }
                                    }}
                                  >
                                    <option value="">Select Field</option>
                                    {selectedCollection.fields.map((f) => (
                                      <option key={f.fieldName} value={f.fieldName}>
                                        {f.fieldTitle.en || f.fieldName}
                                      </option>
                                    ))}
                                  </Field>
                                </td>

                                <td>
                                  <Field
                                    as="select"
                                    className="form-control"
                                    name={`derivedFieldMapping[${index}].derivedField`}
                                    onChange={(e) =>
                                      setFieldValue(
                                        `derivedFieldMapping[${index}].derivedField`,
                                        e.target.value,
                                      )
                                    }
                                  >
                                    <option value="">Select Derived Field</option>
                                    {selectedCollection.utilities.map((f) => (
                                      <option key={f.name} value={f.name}>
                                        {f.name}
                                      </option>
                                    ))}
                                  </Field>
                                </td>

                                <td className="text-center">
                                  {values.derivedFieldMapping.length > 1 &&
                                    index !== values.derivedFieldMapping.length - 1 && (
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-danger"
                                        onClick={() => (isReadOnly ? null : remove(index))}
                                        disabled={isReadOnly}
                                        title={isReadOnly && 'Read-Only Mode'}
                                      >
                                        X
                                      </button>
                                    )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </FieldArray>
                  </ModalBody>

                  <ModalFooter className="d-flex justify-content-between">
                    <button
                      type="button"
                      className="btn btn-xs btn-theme-default-outlined"
                      onClick={toggleModal}
                    >
                      <i className="wb webconnect-times"></i>&nbsp;Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-xs btn-theme-submit"
                      disabled={deleteCollectionDerivedFieldMappingPending || isReadOnly}
                      title={isReadOnly && 'Read-Only Mode'}
                    >
                      <i className="wb webconnect-save-regular"></i>&nbsp;Update
                      <CustomLoader loading={deleteCollectionDerivedFieldMappingPending} />
                    </button>
                  </ModalFooter>
                </Form>
              )}
            </Formik>
          </Modal>
        )}
      </>
    );
  } else {
    return <h5 className="text-center">No Derived Field Mapping!</h5>;
  }
};
